/// <amd-module name="Repos/Changes/Components/ChangesExplorerTree" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { IChange, IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { getChangeTypeDisplayName, getDecorationText } from "Repos/Common/Util/ChangeType";
import {
    IChangesTreeDiscussionIntegration,
    IDiffSummaryDiscussionIntegration,
    IDiscussionIntegrationService
} from "Repos/Discussion/Common/DiscussionContracts";
import { HighlightableSpan } from "Repos/Highlight/Components/HighlightableSpan";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Icon } from "VSSUI/Icon";
import { Intersection } from "VSSUI/Intersection";
import { ListSelection } from "VSSUI/List";
import { ContextualMenu, IMenuItem, IMenuProps, MoreButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { ColumnMore, ColumnMoreWidths, IMeasurementStyle, ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { IExpandable } from "VSSUI/Expandable";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { Button, ExpandableButton } from "VSSUI/Button";
import { IChangeWithDiscussionThread } from "../Contracts";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { Location } from "VSSUI/Utilities/Position";
import { ChangesContext, IChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

function getTreeColumns(
    getItemMenuItems?: (
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ) => IReadonlyObservableArray<IMenuItem>,
    highlightText?: string,
    onCheckmarkChange?: (item: IChange, checkmarked: boolean) => void,
    treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
    rootDiffIntegration?: IDiffSummaryDiscussionIntegration
): ITreeColumn<IChange>[] {
    const columns: ITreeColumn<IChangeWithDiscussionThread>[] = [];

    if (onCheckmarkChange) {
        columns.push({
            id: "checkbox",
            renderCell: (rowIndex, columnIndex, treeColumn, treeItem) => {
                const change = treeItem.underlyingItem.data;
                if (change.thread) {
                    // Don't render checkbox if this is a thread
                    return <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={treeColumn} />;
                } else {
                    return (
                        <Observer checked={change.checkmarked} key={`checkmark-${rowIndex}`}>
                            {({ checked }: { checked: boolean | undefined }) => {
                                return (
                                    <SimpleTableCell
                                        contentClassName={css("no-padding", !checked && "bolt-table-cell-content-reveal")}
                                        columnIndex={columnIndex}
                                        key={columnIndex}
                                        tableColumn={treeColumn}
                                    >
                                        <Checkbox
                                            ariaLabel={Resources.MarkReviewedCheckboxLabel}
                                            checked={checked}
                                            onChange={event => {
                                                event.preventDefault();
                                                onCheckmarkChange(change, !checked);
                                            }}
                                        />
                                    </SimpleTableCell>
                                );
                            }}
                        </Observer>
                    );
                }
            },
            width: 1.25,
            widthStyle: IMeasurementStyle.REM
        });
    }

    columns.push({
        id: "name",
        hierarchical: true,
        name: "Path",
        renderCell: (
            rowIndex: number,
            columnIndex: number,
            treeColumn: ITableColumn<ITreeItemEx<IChangeWithDiscussionThread>>,
            treeItem: ITreeItemEx<IChangeWithDiscussionThread>
        ) => {
            const change = treeItem.underlyingItem.data;
            if (change.thread && change.renderCell) {
                return change.renderCell(rowIndex, columnIndex, treeColumn, treeItem);
            } else {
                const decorationText = getDecorationText(change.changeType, true);

                let itemClassName = "";
                if ((change.changeType & VersionControlChangeType.Delete) === VersionControlChangeType.Delete) {
                    itemClassName = "repos-deleted-item";
                }
                return (
                    <ExpandableTreeCell
                        contentClassName="scroll-hidden flex-grow padding-0"
                        columnIndex={columnIndex}
                        key={columnIndex}
                        treeColumn={treeColumn}
                        treeItem={treeItem}
                    >
                        <div className="padding-vertical-8 scroll-hidden flex-grow flex-row flex-center">
                            <Icon {...change.iconProps} className={css("icon-margin medium", change.iconProps.className)} />
                            <Tooltip
                                text={
                                    change.isFolder
                                        ? change.path
                                        : format(
                                              (change.changeType & VersionControlChangeType.Rename) !== 0
                                                  ? Resources.RenamedFromCommitTreeTooltip
                                                  : Resources.CommitTreeTooltip,
                                              change.path,
                                              getChangeTypeDisplayName(change.changeType),
                                              change.sourcePath
                                          )
                                }
                            >
                                <span className="text-ellipsis">
                                    <HighlightableSpan className={itemClassName} highlight={highlightText} text={change.name} />
                                </span>
                            </Tooltip>
                            {decorationText && (
                                <Pill excludeFocusZone={true} className="margin-horizontal-4" size={PillSize.compact} variant={PillVariant.outlined}>
                                    {decorationText}
                                </Pill>
                            )}
                        </div>
                        {getItemMenuItems && (
                            <ChangesExplorerMoreButton
                                menuPropsProvider={() => ({
                                    id: "commit-header-sub-menu",
                                    items: getItemMenuItems(treeItem.underlyingItem.data, treeDiffIntegration, rootDiffIntegration)
                                })}
                                isVisible={true}
                            />
                        )}
                    </ExpandableTreeCell>
                );
            }
        },
        width: -100
    });

    return columns;
}

export interface IChangesExplorerTreeProps<TVersionDescriptor> {
    className?: string;

    /**
     * Collection of changes being viewed
     */
    changes: IChangeCollection<TVersionDescriptor>;

    /**
     * Service for interacting with discussion threads about the changed files being listed
     */
    discussionService?: IDiscussionIntegrationService;

    /**
     * Selected item in tree
     */
    selectedItem: IReadonlyObservableValue<IChange>;

    /**
     * The thread to select in the tree. If given and found, it will be used instead of selectedItem.
     */
    selectedThreadId?: IReadonlyObservableValue<number | undefined> | undefined;

    /**
     * returns menu items for the selected item
     */
    getItemMenuItems?: (
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ) => IReadonlyObservableArray<IMenuItem>;

    /**
     * Text to highlight in the nodes (search text)
     */
    highlightText?: string;

    /**
     * Should the tree display lines?
     * @default false
     */
    showLines?: boolean;

    /**
     * Callback for when an item is activated
     */
    onActivate(event: React.SyntheticEvent<HTMLElement>, tableRow: IChange): void;

    /**
     * Callback to update the state of the checkmark asociated with a row.
     */
    onCheckmarkChange?(tableRow: IChange, checkmarked: boolean): void;
}

export class ChangesExplorerTree<TVersionDescriptor> extends VssComponent<IChangesExplorerTreeProps<TVersionDescriptor>> {
    private areThreadsLoaded = false;
    private itemProvider: ITreeItemProvider<IChange | ObservableValue<IChange | undefined>>;
    private treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>;
    private rootDiffIntegration?: IDiffSummaryDiscussionIntegration;
    private isSelectedThreadPending = false;
    private selection = new ListSelection({ multiSelect: false, selectOnFocus: false });
    private treeRef = React.createRef<Tree<IChange>>();

    constructor(props: IChangesExplorerTreeProps<TVersionDescriptor>, context: IVssComponentContext) {
        super(props, context);

        const { changes, discussionService } = props;

        // Use direct children of the root to build the item provider, otherwise the tree will use the project folder as root.
        this.itemProvider = new TreeItemProvider<IChange | ObservableValue<IChange | undefined>>(changes.rootTreeItem.childItems!);

        if (discussionService) {
            this.treeDiffIntegration = discussionService.getChangesTreeIntegration(this.itemProvider, this.onThreadsUpdated);
            this.rootDiffIntegration = discussionService.getRootDiffSummaryIntegration();
        }

        this.observe([{ propertyName: "selectedItem", observer: this.setSelectedItem }]);
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.props.selectedItem) {
            const threadId = this.props.selectedThreadId && this.props.selectedThreadId.value;
            if (threadId && !this.areThreadsLoaded) {
                this.isSelectedThreadPending = true;
            } else {
                this.setSelectedItemAndThread(this.props.selectedItem.value, threadId);
            }
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        if (this.treeDiffIntegration) {
            this.treeDiffIntegration.dispose();
            delete this.treeDiffIntegration;
        }

        if (this.rootDiffIntegration) {
            this.rootDiffIntegration.dispose();
            delete this.rootDiffIntegration;
        }
    }

    public componentDidUpdate(prevProps: IChangesExplorerTreeProps<TVersionDescriptor>, prevState: Readonly<{}>, snapshot?: {} | undefined): void {
        super.componentDidUpdate(prevProps, prevState);
        if (isFeatureFlagEnabled(this.context.pageContext, "WebAccess.PullRequest.LoadMoreFiles")) {
            this.itemProvider = new TreeItemProvider<IChange | ObservableValue<IChange | undefined>>(this.props.changes.rootTreeItem.childItems!);
        }

    }

    public render() {
        const { showLines = false } = this.props;

        if (isFeatureFlagEnabled(this.context.pageContext, "WebAccess.PullRequest.LoadMoreFiles")) {
            return (
                <ChangesContext.Consumer>
                    {(changesContext: IChangesContext<TVersionDescriptor>) => (
                        <Observer changes={changesContext.navigationService.getChanges()}>
                            {(selectionProps: { changes: IChangeCollection<TVersionDescriptor> | undefined }) => {
                                return (
                                    <Intersection>
                                        <div className={css(this.props.className, "flex-grow flex-column v-scroll-auto custom-scrollbar scroll-auto-hide")}>
                                            <ChangesExplorerRoot
                                                rootItem={selectionProps.changes!.rootTreeItem}
                                                onActivate={this.props.onActivate}
                                                onCheckmarkChange={this.props.onCheckmarkChange}
                                                getItemMenuItems={this.props.getItemMenuItems}
                                                treeDiffIntegration={this.treeDiffIntegration}
                                                rootDiffIntegration={this.rootDiffIntegration}
                                            />
    
                                            <Tree<IChange | ObservableValue<IChange | undefined>>
                                                className="repos-changes-explorer-tree repos-file-explorer-tree body-xl"
                                                columns={getTreeColumns(
                                                    this.props.getItemMenuItems,
                                                    this.props.highlightText,
                                                    this.props.onCheckmarkChange,
                                                    this.treeDiffIntegration,
                                                    this.rootDiffIntegration
                                                )}
                                                itemProvider={this.itemProvider}
                                                onActivate={this.onActivate}
                                                onToggle={this.onToggle}
                                                ref={this.treeRef}
                                                selection={this.selection}
                                                showHeader={false}
                                                showLines={showLines}
                                            />
                                        </div>
                                    </Intersection>
                                );
                            }}
                        </Observer>
                    )}
                </ChangesContext.Consumer>
            );
        }

        return (
            <Intersection>
                <div className={css(this.props.className, "flex-grow flex-column v-scroll-auto custom-scrollbar scroll-auto-hide")}>
                    <ChangesExplorerRoot
                        rootItem={this.props.changes.rootTreeItem}
                        onActivate={this.props.onActivate}
                        onCheckmarkChange={this.props.onCheckmarkChange}
                        getItemMenuItems={this.props.getItemMenuItems}
                        treeDiffIntegration={this.treeDiffIntegration}
                        rootDiffIntegration={this.rootDiffIntegration}
                    />

                    <Tree<IChange | ObservableValue<IChange | undefined>>
                        className="repos-changes-explorer-tree repos-file-explorer-tree body-xl"
                        columns={getTreeColumns(
                            this.props.getItemMenuItems,
                            this.props.highlightText,
                            this.props.onCheckmarkChange,
                            this.treeDiffIntegration,
                            this.rootDiffIntegration
                        )}
                        itemProvider={this.itemProvider}
                        onActivate={this.onActivate}
                        onToggle={this.onToggle}
                        ref={this.treeRef}
                        selection={this.selection}
                        showHeader={false}
                        showLines={showLines}
                    />
                </div>
            </Intersection>
        );
    }

    private onThreadsUpdated = () => {
        this.areThreadsLoaded = true;
        if (this.isSelectedThreadPending) {
            this.setSelectedItemAndThread(this.props.selectedItem.value, this.props.selectedThreadId && this.props.selectedThreadId.value);
            this.isSelectedThreadPending = false;
        }
    };

    private setSelectedItem = (selectedItem: IChangeWithDiscussionThread): void => {
        this.setSelectedItemAndThread(selectedItem, undefined);
    };

    private setSelectedItemAndThread(selectedItem: IChangeWithDiscussionThread, selectedThreadId: number | undefined): void {
        const items = this.itemProvider.value;
        for (let i = 0, l = items.length; i < l; i++) {
            let found = false;
            const treeItem = ObservableLike.getValue(this.itemProvider.value[i].underlyingItem.data) as IChangeWithDiscussionThread;

            if (treeItem && selectedItem.path === treeItem.path) {
                if (!selectedThreadId && selectedItem.thread) {
                    selectedThreadId = selectedItem.thread.id;
                }

                if (selectedThreadId) {
                    if (treeItem.thread && selectedThreadId === treeItem.thread.id) {
                        found = true;
                    }
                } else if (!treeItem.thread) {
                    found = true;
                }
            }

            if (found) {
                this.selection.select(i);
                if (this.treeRef.current) {
                    this.treeRef.current.scrollIntoView(i, {
                        block: "nearest"
                    });
                }
                return;
            }
        }

        // Item not found, clear any existing selection
        this.selection.clear();
    }

    private onToggle = (event: React.SyntheticEvent<HTMLElement> | undefined, treeItem: ITreeItemEx<IChange>) => {
        this.itemProvider.toggle(treeItem.underlyingItem);
    };

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITreeRow<IChange>): void => {
        this.props.onActivate(event, tableRow.data.underlyingItem.data);
    };
}

interface IChangesExplorerRootProps {
    /**
     * Root element of the original tree.
     */
    rootItem: ITreeItem<IChange>;
    /**
     * Callback for making the root clickable.
     */
    onActivate: (event: React.SyntheticEvent<HTMLElement>, data: IChange) => void;
    /**
     * Returns menu items for the selected item.
     */
    getItemMenuItems?: (
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ) => IReadonlyObservableArray<IMenuItem>;
    /**
     * Callback to update the state of the checkmark asociated with a row.
     */
    onCheckmarkChange?(tableRow: IChange, checkmarked: boolean): void;
    /**
     * Necessary for rendering the header's dropdown menu.
     */
    treeDiffIntegration: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>> | undefined;
    rootDiffIntegration?: IDiffSummaryDiscussionIntegration;
}

function ChangesExplorerRoot(props: IChangesExplorerRootProps): JSX.Element {
    const root = props.rootItem.data;

    return (
        <FocusZone direction={FocusZoneDirection.Horizontal}>
            <div className="repos-changes-explorer-tree-root">
                <Observer checked={root.checkmarked}>
                    {({ checked }: { checked: boolean | undefined }) => {
                        return (
                            <div className="bolt-table-row flex-row padding-horizontal-8 padding-vertical-4 flex-center">
                                {props.onCheckmarkChange && (
                                    <Checkbox
                                        ariaLabel={Resources.MarkReviewedCheckboxLabel}
                                        checked={checked}
                                        onChange={event => {
                                            event.preventDefault();
                                            props.onCheckmarkChange!(root, !checked);
                                        }}
                                    />
                                )}
                                <div className="padding-vertical-8 padding-horizontal-4 scroll-hidden flex-row flex-grow">
                                    <Button
                                        className="repos-changes-explorer-tree-root-content padding-0 scroll-hidden flex-row flex-center"
                                        subtle={true}
                                        onClick={event => props.onActivate(event, root)}
                                    >
                                        <Icon {...root.iconProps} className={css("icon-margin medium", root.iconProps.className)}></Icon>
                                        <span className="body-xl font-weight-normal text-ellipsis">{root.name}</span>
                                    </Button>
                                </div>
                                {props.getItemMenuItems && (
                                    <ChangesExplorerMoreButton
                                        isVisible={false}
                                        menuPropsProvider={() => ({
                                            id: "commit-header-sub-menu",
                                            items: props.getItemMenuItems!(root, props.treeDiffIntegration, props.rootDiffIntegration)
                                        })}
                                    />
                                )}
                            </div>
                        );
                    }}
                </Observer>
            </div>
        </FocusZone>
    );
}

interface IChangesExplorerMoreButtonProps {
    /** Callback that returns the menu props. Necessary for updating the button's callout menu. */
    menuPropsProvider: () => IMenuProps;
    isVisible: boolean;
}

function ChangesExplorerMoreButton(props: IChangesExplorerMoreButtonProps): JSX.Element {
    const buttonRef = React.createRef<ExpandableButton>();
    return (
        <div className={` ${props.isVisible ? "bolt-table-cell-content-appear bolt-table-cell-content-reveal":"" } flex-row justify-center`}>
            <ExpandableButton
                ariaLabel={Resources.MoreTooltip}
                className="bolt-table-button-more"
                excludeTabStop={true}
                hideDropdownIcon={true}
                iconProps={{ className: "small", iconName: "MoreVertical" }}
                ref={buttonRef}
                renderCallout={(dropdown: IExpandable, dropdownId: string, anchorElement?: HTMLElement) => {
                    return (
                        <ContextualMenu
                            anchorElement={anchorElement}
                            anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                            menuProps={props.menuPropsProvider()}
                            menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
                            onDismiss={dropdown.collapse}
                        />
                    );
                }}
                subtle={true}
                tooltipProps={{ text: Resources.MoreTooltip }}
            />
        </div>
    );
}
